import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  FiArrowLeft,
  FiChevronRight,
  FiLayers,
  FiSettings,
  FiList,
  FiRefreshCw,
  FiSave,
  FiSearch,
  FiCopy,
  FiCheck,
  FiInfo,
  FiEye,
  FiAlertTriangle,
  FiCalendar,
  FiClock,
  FiAward,
  FiZap,
  FiPlay,
  FiPercent,
} from "react-icons/fi";
import {
  getSpinwheelSettings,
  updateSpinwheelSettings,
  getSpinwheelHistory,
} from "../api/tapGameApi";
import { getEvents } from "../api/eventApi";
import { formatUtcDateTimeWithSeconds } from "../utils/dateUtils";
import GameAnalyticsCharts from "./components/GameAnalyticsCharts";
import GameSessionDetailModal from "./components/GameSessionDetailModal";
import dayjs from "dayjs";

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  try {
    return formatUtcDateTimeWithSeconds(dateStr);
  } catch {
    return String(dateStr);
  }
};

const DEFAULT_SLICES = [0, 10, 0, 50, 0, 100, 0, 500, 0, 1000];
const SLICE_COLORS = [
  "#ef4444", // 0: red
  "#3b82f6", // 1: blue
  "#6b7280", // 2: gray
  "#10b981", // 3: emerald
  "#8b5cf6", // 4: purple
  "#f59e0b", // 5: amber
  "#ec4899", // 6: pink
  "#06b6d4", // 7: cyan
  "#64748b", // 8: slate
  "#eab308", // 9: gold
];

const DEFAULT_PROBABILITIES = {
  lossPercentage: 50,
  tenPointsPercentage: 25,
  fiftyPointsPercentage: 12,
  hundredPointsPercentage: 8,
  fiveHundredPointsPercentage: 4,
  thousandPointsPercentage: 1,
};

export default function SpinWheelGame() {
  const navigate = useNavigate();

  const handleGoBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate("/games");
    }
  };

  // Active Tab: "settings" or "history"
  const [activeTab, setActiveTab] = useState("settings");

  // Settings State
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [formSettings, setFormSettings] = useState({
    name: "Lucky Spin Wheel",
    description: "Spin the fortune wheel to win up to 1000 event points!",
    spinTime: 5,
    cooldownSeconds: 180,
    minPoints: 0,
    maxPoints: 1000,
    isActive: true,
  });

  // 10-slice configuration array
  const [slices, setSlices] = useState(DEFAULT_SLICES);

  // Probability distribution state (6 tiers)
  const [pointProbabilities, setPointProbabilities] = useState(DEFAULT_PROBABILITIES);

  // Event State: Default to active event
  const [eventsList, setEventsList] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState("");

  // Stats State
  const [stats, setStats] = useState({
    todaySessions: 0,
    todayPoints: 0,
    eventTotalPoints: 0,
    eventTotalSessions: 0,
    totalSessions: 0,
    completedSessions: 0,
    totalPointsAwarded: 0,
  });

  // Daily Analytics Data for Visual Charts
  const [dailyStats, setDailyStats] = useState([]);
  const [analyticsHistory, setAnalyticsHistory] = useState([]);

  // History State
  const [historyLoading, setHistoryLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(1);
  const perPage = 20;
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 1,
    page: 1,
    perPage: 20,
  });

  // Interactive Simulator state
  const [spinning, setSpinning] = useState(false);
  const [wheelRotation, setWheelRotation] = useState(0);
  const [simulationResult, setSimulationResult] = useState(null);

  // UI state
  const [copiedSessionId, setCopiedSessionId] = useState("");
  const [selectedSessionModal, setSelectedSessionModal] = useState(null);
  const searchDebounceTimer = useRef(null);

  // Compute stats helper
  const computeSpinwheelStats = (list = [], serverStats = null, currentDailyStats = [], prevStats = null) => {
    const todayStr = dayjs().format("YYYY-MM-DD");

    // 1. Check server-provided stats
    const serverTodaySessions = serverStats?.todaySessions;
    const serverTodayPoints = serverStats?.todayPoints;
    const serverEventPoints = Number(
      serverStats?.eventTotalPoints ?? serverStats?.truePointsAwarded ?? serverStats?.totalPointsAwarded ?? serverStats?.totalSpinwheelPointsAwarded ?? 0
    );
    const serverEventSessions = Number(
      serverStats?.eventTotalSessions ?? serverStats?.totalSessions ?? serverStats?.totalSpinwheelSessions ?? 0
    );

    // 2. Client fallback for today's stats if server did not calculate them
    let todaySessionsCalc = 0;
    let todayPointsCalc = 0;

    const sourceDaily = Array.isArray(currentDailyStats) && currentDailyStats.length > 0 ? currentDailyStats : [];
    const todayDailyRow = sourceDaily.find(
      (d) => d?.date === todayStr || dayjs(d?.date).format("YYYY-MM-DD") === todayStr
    );

    if (todayDailyRow) {
      todaySessionsCalc = Number(todayDailyRow.sessions || todayDailyRow.count || 0);
      todayPointsCalc = Number(todayDailyRow.points || todayDailyRow.claimedPoints || 0);
    } else if (Array.isArray(list) && list.length > 0) {
      const todayList = list.filter((row) => {
        const d = row.createdAt || row.startedAt || row.completedAt;
        return d && dayjs(d).format("YYYY-MM-DD") === todayStr;
      });
      todaySessionsCalc = todayList.length;
      todayPointsCalc = todayList.reduce((sum, r) => {
        if (r.status === "completed" || r.status === "Claimed" || r.status === "claimed") {
          return sum + Number(r.claimedPoints ?? r.pointsAwarded ?? r.targetPoints ?? 0);
        }
        return sum;
      }, 0);
    }

    const completedList = list.filter(
      (h) => h.status === "completed" || h.status === "Claimed" || h.status === "claimed"
    );
    const listEventPoints = completedList.reduce(
      (acc, h) =>
        acc +
        Number(h.claimedPoints ?? h.pointsAwarded ?? h.targetPoints ?? h.points ?? 0),
      0
    );

    return {
      todaySessions: serverTodaySessions != null ? Number(serverTodaySessions) : (prevStats?.todaySessions ?? todaySessionsCalc),
      todayPoints: serverTodayPoints != null ? Number(serverTodayPoints) : (prevStats?.todayPoints ?? todayPointsCalc),
      eventTotalPoints: serverEventPoints > 0 ? serverEventPoints : (listEventPoints > 0 ? listEventPoints : (prevStats?.eventTotalPoints ?? 0)),
      eventTotalSessions: serverEventSessions > 0 ? serverEventSessions : (list.length > 0 ? list.length : (prevStats?.eventTotalSessions ?? 0)),
      totalSessions: serverEventSessions > 0 ? serverEventSessions : (list.length > 0 ? list.length : (prevStats?.totalSessions ?? 0)),
      completedSessions: Number(serverStats?.completedSessions ?? completedList.length ?? prevStats?.completedSessions ?? 0),
      totalPointsAwarded: serverEventPoints > 0 ? serverEventPoints : (listEventPoints > 0 ? listEventPoints : (prevStats?.totalPointsAwarded ?? 0)),
    };
  };

  // Fetch Settings
  const fetchSettings = useCallback(async (showToast = false) => {
    setSettingsLoading(true);
    try {
      const res = await getSpinwheelSettings();
      if (res?.status && res.data) {
        const d = res.data;
        setFormSettings((prev) => ({
          ...prev,
          name: d.name || "Lucky Spin Wheel",
          description: d.description || "Spin the fortune wheel to win up to 1000 event points!",
          spinTime: Number(d.spinTime ?? d.gameTime ?? 5),
          cooldownSeconds: Number(d.cooldownSeconds ?? 180),
          minPoints: Number(d.minPoints ?? 0),
          maxPoints: Number(d.maxPoints ?? 1000),
          isActive: Boolean(d.isActive === 1 || d.isActive === true || d.isActive === "1"),
        }));

        if (Array.isArray(d.slices) && d.slices.length === 10) {
          setSlices(d.slices.map(Number));
        }

        if (d.pointProbabilities && typeof d.pointProbabilities === "object") {
          const p = d.pointProbabilities;
          setPointProbabilities({
            lossPercentage: Number(p.lossPercentage ?? 50),
            tenPointsPercentage: Number(p.tenPointsPercentage ?? 25),
            fiftyPointsPercentage: Number(p.fiftyPointsPercentage ?? 12),
            hundredPointsPercentage: Number(p.hundredPointsPercentage ?? 8),
            fiveHundredPointsPercentage: Number(p.fiveHundredPointsPercentage ?? 4),
            thousandPointsPercentage: Number(p.thousandPointsPercentage ?? 1),
          });
        }

        if (d.stats && (!selectedEventId || selectedEventId === "all")) {
          setStats((prev) => computeSpinwheelStats([], d.stats, d.dailyStats || [], prev));
        }
        if (showToast) toast.success("Spin Wheel settings reloaded!");
      }
    } catch (err) {
      console.warn("Failed to load Spin Wheel settings, using defaults:", err.message);
      if (showToast) {
        toast.error(err.message || "Failed to load Spin Wheel settings");
      }
    } finally {
      setSettingsLoading(false);
    }
  }, [selectedEventId]);

  // Fetch History
  const fetchHistory = useCallback(
    async (showToast = false, overrideEventId = null) => {
      setHistoryLoading(true);
      const activeEventFilter = overrideEventId !== null ? overrideEventId : selectedEventId;
      try {
        const res = await getSpinwheelHistory({
          page,
          perPage,
          search: debouncedSearch,
          status: statusFilter,
          startDate,
          endDate,
          eventId: activeEventFilter && activeEventFilter !== "all" ? activeEventFilter : undefined,
        });

        let historyList = [];
        let totalCount = 0;
        let totalPages = 1;

        if (res?.status) {
          if (Array.isArray(res.data)) {
            historyList = res.data;
          } else if (res.data?.history && Array.isArray(res.data.history)) {
            historyList = res.data.history;
          }

          const serverPagination = res.pagination || res.data?.pagination || {};
          totalCount =
            typeof serverPagination.total === "number"
              ? serverPagination.total
              : typeof serverPagination.totalRecords === "number"
              ? serverPagination.totalRecords
              : typeof res.total === "number"
              ? res.total
              : historyList.length;

          totalPages =
            typeof serverPagination.lastPage === "number"
              ? serverPagination.lastPage
              : typeof serverPagination.totalPages === "number"
              ? serverPagination.totalPages
              : Math.max(1, Math.ceil(totalCount / perPage));

          setHistory(historyList);
          setAnalyticsHistory(historyList);

          if (Array.isArray(res.dailyStats) && res.dailyStats.length > 0) {
            setDailyStats(res.dailyStats);
          }

          setPagination({
            total: totalCount,
            totalPages,
            page,
            perPage,
          });

          setStats((prev) => computeSpinwheelStats(historyList, res.stats || res.data?.stats, res.dailyStats || [], prev));

          if (showToast) toast.success("History refreshed!");
        }
      } catch (err) {
        console.warn("Failed to load Spin Wheel history:", err.message);
        if (showToast) toast.error(err.message || "Failed to load history");
      } finally {
        setHistoryLoading(false);
      }
    },
    [page, debouncedSearch, statusFilter, startDate, endDate, selectedEventId]
  );

  // Initial Load - Fetch Events & set default running event
  useEffect(() => {
    let isMounted = true;
    getEvents({ limit: 100, page: 1, status: "all" })
      .then((res) => {
        if (!isMounted) return;
        const rawData = res?.data || res;
        const evList = Array.isArray(rawData?.events)
          ? rawData.events
          : Array.isArray(rawData)
          ? rawData
          : Array.isArray(res?.events)
          ? res.events
          : [];
        setEventsList(evList);

        const running = evList.find((e) => e.status?.toLowerCase() === "running");
        if (running) {
          const initialEventId = String(running.eventId || running.id);
          setSelectedEventId(initialEventId);
          fetchHistory(false, initialEventId);
        } else if (evList.length > 0) {
          const initialEventId = String(evList[0].eventId || evList[0].id);
          setSelectedEventId(initialEventId);
          fetchHistory(false, initialEventId);
        } else {
          setSelectedEventId("all");
          fetchHistory(false, "all");
        }
      })
      .catch((err) => {
        console.warn("Could not load events list:", err.message);
        if (isMounted) {
          setEventsList([]);
          setSelectedEventId("all");
          fetchHistory(false, "all");
        }
      });

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch settings on mount
  useEffect(() => {
    fetchSettings(false);
  }, [fetchSettings]);

  // Handle Event Selector Change
  const handleEventChange = (newEventId) => {
    setSelectedEventId(newEventId);
    setPage(1);
    fetchHistory(false, newEventId);
  };

  // Debounce search
  useEffect(() => {
    if (searchDebounceTimer.current) clearTimeout(searchDebounceTimer.current);
    searchDebounceTimer.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(searchDebounceTimer.current);
  }, [search]);

  useEffect(() => {
    fetchSettings(false);
    fetchHistory(false);
  }, [fetchSettings, fetchHistory]);

  useEffect(() => {
    if (activeTab === "history") {
      fetchHistory(false);
    }
  }, [activeTab, fetchHistory]);

  // Handle Slice Point Change
  const handleSliceChange = (index, value) => {
    const num = Math.max(0, parseInt(value, 10) || 0);
    const updated = [...slices];
    updated[index] = num;
    setSlices(updated);
  };

  // Probability sum helper
  const totalProbability = Object.values(pointProbabilities).reduce(
    (sum, val) => sum + (Number(val) || 0),
    0
  );
  const isProbabilityValid = Math.abs(totalProbability - 100) < 0.01;

  // Auto-balance probabilities
  const handleAutoBalance = () => {
    const keys = Object.keys(pointProbabilities);
    const currentSum = Object.values(pointProbabilities).reduce((a, b) => a + (Number(b) || 0), 0);
    if (currentSum === 0) {
      setPointProbabilities(DEFAULT_PROBABILITIES);
      toast.success("Reset to default 100% distribution!");
      return;
    }

    let remaining = 100;
    const balanced = {};
    keys.forEach((k, idx) => {
      if (idx === keys.length - 1) {
        balanced[k] = Math.max(0, remaining);
      } else {
        const scaled = Math.round(((Number(pointProbabilities[k]) || 0) / currentSum) * 100);
        balanced[k] = scaled;
        remaining -= scaled;
      }
    });

    setPointProbabilities(balanced);
    toast.success("Probabilities auto-balanced to 100%!");
  };

  const handleResetProbabilities = () => {
    setPointProbabilities(DEFAULT_PROBABILITIES);
    setSlices(DEFAULT_SLICES);
    toast.success("Reset to recommended Spin Wheel distribution!");
  };

  // Interactive Test Spin Simulator
  const handleTestSpin = () => {
    if (spinning) return;
    setSpinning(true);
    setSimulationResult(null);

    // Pick winning slice based on probabilities
    const rand = Math.random() * 100;
    let targetPts = 0;
    const p = pointProbabilities;

    const tThousand = p.thousandPointsPercentage;
    const tFiveHundred = tThousand + p.fiveHundredPointsPercentage;
    const tHundred = tFiveHundred + p.hundredPointsPercentage;
    const tFifty = tHundred + p.fiftyPointsPercentage;
    const tTen = tFifty + p.tenPointsPercentage;

    if (rand < tThousand) targetPts = 1000;
    else if (rand < tFiveHundred) targetPts = 500;
    else if (rand < tHundred) targetPts = 100;
    else if (rand < tFifty) targetPts = 50;
    else if (rand < tTen) targetPts = 10;
    else targetPts = 0;

    // Find indices that match targetPts, or fallback
    const matchingIndices = [];
    slices.forEach((pts, idx) => {
      if (pts === targetPts) matchingIndices.push(idx);
    });

    const chosenIndex =
      matchingIndices.length > 0
        ? matchingIndices[Math.floor(Math.random() * matchingIndices.length)]
        : Math.floor(Math.random() * 10);

    const actualPts = slices[chosenIndex] ?? 0;

    // Each slice is 36 degrees (360 / 10). Top pointer is at 0/360 degrees.
    // Slices are laid out clockwise starting from index 0.
    // To land slice `chosenIndex` at the top pointer (0 deg):
    const sliceAngle = 36;
    const sliceCenter = chosenIndex * sliceAngle + sliceAngle / 2;
    const extraRounds = 5 * 360; // 5 full spins
    const targetRot = wheelRotation + extraRounds + (360 - (sliceCenter % 360));

    setWheelRotation(targetRot);

    setTimeout(() => {
      setSpinning(false);
      setSimulationResult({
        sliceIndex: chosenIndex,
        points: actualPts,
        isWin: actualPts > 0,
      });
      if (actualPts > 0) {
        toast.success(`🎉 Test Spin Result: Won +${actualPts} PTS (Slice #${chosenIndex})`);
      } else {
        toast("Better Luck Next Time! (0 PTS)", { icon: "🎡" });
      }
    }, 3200);
  };

  // Save Settings
  const handleSaveSettings = async (e) => {
    e.preventDefault();

    if (!isProbabilityValid) {
      toast.error(`Probabilities sum is ${totalProbability}%. It must equal exactly 100%!`);
      return;
    }

    if (slices.length !== 10) {
      toast.error("Wheel must have exactly 10 slices!");
      return;
    }

    setSavingSettings(true);
    try {
      const payload = {
        name: formSettings.name.trim(),
        description: formSettings.description.trim(),
        spinTime: Number(formSettings.spinTime),
        gameTime: Number(formSettings.spinTime),
        cooldownSeconds: Number(formSettings.cooldownSeconds),
        minPoints: Math.min(...slices),
        maxPoints: Math.max(...slices),
        isActive: formSettings.isActive,
        slices: slices.map(Number),
        pointProbabilities,
      };

      const res = await updateSpinwheelSettings(payload);
      if (res?.status) {
        toast.success(res.message || "Lucky Spin Wheel settings saved successfully!");
        fetchSettings(false);
      } else {
        toast.error(res?.message || "Failed to update Spin Wheel settings");
      }
    } catch (err) {
      console.error("Error saving Spin Wheel settings:", err);
      toast.error(err.message || "Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const copySessionId = (sid) => {
    if (!sid) return;
    navigator.clipboard.writeText(sid);
    setCopiedSessionId(sid);
    toast.success("Session ID copied!");
    setTimeout(() => setCopiedSessionId(""), 2000);
  };

  // Render Fortune Wheel SVG
  const renderWheelSvg = () => {
    const size = 300;
    const center = size / 2;
    const radius = 135;
    const numSlices = 10;
    const anglePerSlice = (2 * Math.PI) / numSlices;

    return (
      <svg
        viewBox={`0 0 ${size} ${size}`}
        className="w-full h-full drop-shadow-xl"
        style={{
          transform: `rotate(${wheelRotation}deg)`,
          transition: spinning ? "transform 3.2s cubic-bezier(0.15, 0.9, 0.25, 1)" : "none",
        }}
      >
        <circle cx={center} cy={center} r={radius + 8} fill="#1f2937" stroke="#fbbf24" strokeWidth="6" />
        {slices.map((pts, idx) => {
          const startAngle = idx * anglePerSlice - Math.PI / 2;
          const endAngle = (idx + 1) * anglePerSlice - Math.PI / 2;
          const midAngle = (startAngle + endAngle) / 2;

          const x1 = center + radius * Math.cos(startAngle);
          const y1 = center + radius * Math.sin(startAngle);
          const x2 = center + radius * Math.cos(endAngle);
          const y2 = center + radius * Math.sin(endAngle);

          const textRadius = radius * 0.68;
          const textX = center + textRadius * Math.cos(midAngle);
          const textY = center + textRadius * Math.sin(midAngle);
          const textRotation = (midAngle * 180) / Math.PI + 90;

          const fillColor = SLICE_COLORS[idx % SLICE_COLORS.length];

          return (
            <g key={idx}>
              <path
                d={`M ${center} ${center} L ${x1} ${y1} A ${radius} ${radius} 0 0 1 ${x2} ${y2} Z`}
                fill={fillColor}
                stroke="#111827"
                strokeWidth="2"
              />
              <text
                x={textX}
                y={textY}
                fill="#ffffff"
                fontSize="12"
                fontWeight="900"
                fontFamily="sans-serif"
                textAnchor="middle"
                dominantBaseline="central"
                transform={`rotate(${textRotation}, ${textX}, ${textY})`}
                className="select-none"
              >
                {pts > 0 ? `${pts}` : "0"}
              </text>
            </g>
          );
        })}
        {/* Center Hub */}
        <circle cx={center} cy={center} r="22" fill="#111827" stroke="#ffffff" strokeWidth="3" />
        <circle cx={center} cy={center} r="10" fill="#ffff00" />
      </svg>
    );
  };

  // Resolve current active event label
  const activeRunningEvent = useMemo(() => {
    if (!Array.isArray(eventsList)) return null;
    return eventsList.find((e) => e.status?.toLowerCase() === "running");
  }, [eventsList]);

  const selectedEventObj = useMemo(() => {
    if (!selectedEventId || selectedEventId === "all" || !Array.isArray(eventsList)) return null;
    return eventsList.find(
      (e) => String(e.eventId) === String(selectedEventId) || String(e.id) === String(selectedEventId)
    );
  }, [selectedEventId, eventsList]);

  const selectedEventName = selectedEventObj
    ? selectedEventObj.eventName || `Event #${selectedEventObj.id}`
    : selectedEventId === "all"
    ? "All Events"
    : "Active Event";

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />

        <div className="p-5 sm:p-7 space-y-5 w-full">
          {/* Breadcrumb Navigation */}
          <div className="flex items-center gap-2 text-xs md:text-sm text-gray-500">
            <button
              onClick={handleGoBack}
              className="flex items-center gap-1 hover:text-black transition cursor-pointer p-1 -ml-1 rounded hover:bg-gray-200"
              title="Go back"
            >
              <FiArrowLeft className="h-4 w-4" />
              <span>Back</span>
            </button>
            <FiChevronRight className="h-3 w-3 text-gray-400" />
            <Link to="/dashboard" className="hover:text-black transition">
              Dashboard
            </Link>
            <FiChevronRight className="h-3 w-3 text-gray-400" />
            <Link to="/games" className="hover:text-black transition">
              Games Management
            </Link>
            <FiChevronRight className="h-3 w-3 text-gray-400" />
            <span className="text-gray-900 font-semibold">Lucky Spin Wheel</span>
          </div>

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center text-2xl shadow-2xs">
                🎡
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-black text-gray-900">
                  Lucky Spin Wheel Game
                </h1>
                <p className="text-xs text-gray-500 mt-0.5">
                  Manage 10-slice fortune wheel values, tier probabilities, spin rules, and audit logs.
                </p>
              </div>
            </div>

            {/* Quick Game Switcher & Refresh */}
            <div className="flex items-center gap-2.5 flex-wrap">
              <div className="flex items-center bg-gray-200/80 p-1 rounded-xl text-xs font-bold">
                <Link
                  to="/games"
                  className="px-2.5 py-1.5 rounded-lg text-gray-600 hover:text-black hover:bg-white/60 transition flex items-center gap-1"
                  title="All Games Priority & Overview"
                >
                  <FiLayers className="h-3.5 w-3.5" /> All Games
                </Link>
                <Link
                  to="/boom-cards"
                  className="px-3 py-1.5 rounded-lg text-gray-600 hover:text-black hover:bg-white/60 transition flex items-center gap-1.5"
                >
                  <span>🃏</span> Boom Cards
                </Link>
                <Link
                  to="/tap-game"
                  className="px-3 py-1.5 rounded-lg text-gray-600 hover:text-black hover:bg-white/60 transition flex items-center gap-1.5"
                >
                  <span>🎮</span> Tap Game
                </Link>
                <Link
                  to="/slots-game"
                  className="px-3 py-1.5 rounded-lg text-gray-600 hover:text-black hover:bg-white/60 transition flex items-center gap-1.5"
                >
                  <span>🎰</span> 777 Slots
                </Link>
                <button
                  type="button"
                  className="px-3 py-1.5 rounded-lg bg-white text-black shadow-xs flex items-center gap-1.5"
                >
                  <span>🎡</span> Spin Wheel
                </button>
              </div>

              <button
                onClick={() => {
                  if (activeTab === "settings") fetchSettings(true);
                  if (activeTab === "history") fetchHistory(true);
                }}
                disabled={settingsLoading || historyLoading}
                className="flex items-center gap-1.5 text-xs font-bold text-gray-700 bg-white hover:bg-gray-50 border border-gray-200 px-3.5 py-2 rounded-xl transition shadow-2xs cursor-pointer"
              >
                <FiRefreshCw
                  size={13}
                  className={`h-3.5 w-3.5 ${
                    settingsLoading || historyLoading ? "animate-spin text-[#999900]" : ""
                  }`}
                />
                <span>Refresh</span>
              </button>
            </div>
          </div>

          {/* 5 Core Items in Single Unified Row (Event Selector + 4 Key Metrics) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3.5">
            {/* 1. Event Selector Card */}
            <div className="bg-white rounded-2xl shadow-xs border border-amber-200/80 bg-gradient-to-br from-white to-amber-50/20 p-4 flex flex-col justify-between hover:border-amber-300 transition">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-amber-800 uppercase tracking-wider flex items-center gap-1.5">
                  <FiCalendar size={13} className="text-amber-600" />
                  <span>Select Event</span>
                </span>
                {selectedEventObj?.status && (
                  <span
                    className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${
                      selectedEventObj.status.toLowerCase() === "running"
                        ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                        : "bg-gray-100 text-gray-600 border border-gray-200"
                    }`}
                  >
                    {selectedEventObj.status}
                  </span>
                )}
              </div>
              <div className="mt-1.5">
                <select
                  value={selectedEventId}
                  onChange={(e) => handleEventChange(e.target.value)}
                  className="w-full bg-white hover:bg-gray-50 border border-gray-300 text-gray-900 font-bold text-xs rounded-xl px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#ffff00] cursor-pointer shadow-2xs"
                >
                  {activeRunningEvent && (
                    <option value={activeRunningEvent.eventId || activeRunningEvent.id}>
                      🟢 {activeRunningEvent.eventName || `Event #${activeRunningEvent.id}`}
                    </option>
                  )}
                  <option value="all">🏆 All Events (Global)</option>
                  {(Array.isArray(eventsList) ? eventsList : [])
                    .filter(
                      (ev) =>
                        !activeRunningEvent ||
                        String(ev.eventId || ev.id) !==
                          String(activeRunningEvent.eventId || activeRunningEvent.id)
                    )
                    .map((ev) => {
                      const isCompleted = ev.status?.toLowerCase() === "completed";
                      return (
                        <option key={ev.eventId || ev.id} value={ev.eventId || ev.id}>
                          {isCompleted ? "⚪ " : "🟡 "}
                          {ev.eventName || `Event #${ev.id}`}
                        </option>
                      );
                    })}
                </select>
              </div>
              <p className="text-[11px] text-gray-400 mt-1.5 font-medium truncate">
                Filter metrics &amp; charts
              </p>
            </div>

            {/* 2. Today Total Session */}
            <div className="bg-white rounded-2xl shadow-xs border border-gray-200/80 p-4 flex flex-col justify-between hover:border-gray-300 transition">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                  Today Total Sessions
                </span>
                <div className="w-6 h-6 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                  <FiClock size={13} />
                </div>
              </div>
              <p className="text-2xl font-black text-gray-900 font-mono mt-1.5">
                {(stats?.todaySessions ?? 0).toLocaleString()}
              </p>
              <p className="text-[11px] text-gray-400 mt-1.5 font-medium">
                Sessions played today (UTC)
              </p>
            </div>

            {/* 3. Today's Awarded Points */}
            <div className="bg-white rounded-2xl shadow-xs border border-emerald-200/80 bg-gradient-to-br from-white to-emerald-50/20 p-4 flex flex-col justify-between hover:border-emerald-300 transition">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-emerald-800 uppercase tracking-wider">
                  Today's Awarded Points
                </span>
                <div className="w-6 h-6 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold">
                  <FiZap size={13} />
                </div>
              </div>
              <p className="text-2xl font-black text-emerald-700 font-mono mt-1.5">
                +{ (stats?.todayPoints ?? 0).toLocaleString() }{" "}
                <span className="text-[11px] font-sans text-emerald-600 font-bold">PTS</span>
              </p>
              <p className="text-[11px] text-emerald-700/80 mt-1.5 font-medium">
                Claimed by players today
              </p>
            </div>

            {/* 4. Event's Total Points */}
            <div className="bg-white rounded-2xl shadow-xs border border-purple-200/80 bg-gradient-to-br from-white to-purple-50/20 p-4 flex flex-col justify-between hover:border-purple-300 transition">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-purple-800 uppercase tracking-wider">
                  Event's Total Points
                </span>
                <div className="w-6 h-6 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center font-bold">
                  <FiAward size={13} />
                </div>
              </div>
              <p className="text-2xl font-black text-purple-700 font-mono mt-1.5">
                {(stats?.eventTotalPoints ?? 0).toLocaleString()}{" "}
                <span className="text-[11px] font-sans text-purple-600 font-bold">PTS</span>
              </p>
              <p className="text-[11px] text-purple-700/80 mt-1.5 font-medium truncate">
                Cumulative in {selectedEventName}
              </p>
            </div>

            {/* 5. Event's Total Sessions */}
            <div className="bg-white rounded-2xl shadow-xs border border-amber-200/80 bg-gradient-to-br from-white to-amber-50/20 p-4 flex flex-col justify-between hover:border-amber-300 transition">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-amber-800 uppercase tracking-wider">
                  Event's Total Sessions
                </span>
                <div className="w-6 h-6 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center font-bold">
                  <FiLayers size={13} />
                </div>
              </div>
              <p className="text-2xl font-black text-amber-700 font-mono mt-1.5">
                {(stats?.eventTotalSessions ?? 0).toLocaleString()}
              </p>
              <p className="text-[11px] text-amber-700/80 mt-1.5 font-medium truncate">
                All rounds in {selectedEventName}
              </p>
            </div>
          </div>

          {/* Analytics Visual Charts */}
          <GameAnalyticsCharts
            dailyStats={dailyStats}
            historyList={analyticsHistory}
            selectedEvent={selectedEventObj}
            selectedEventId={selectedEventId}
            themeColor="amber"
          />

          {/* Tab Switcher */}
          <div className="flex gap-2 bg-white border border-gray-200 rounded-xl p-1 w-fit shadow-xs">
            <button
              type="button"
              onClick={() => setActiveTab("settings")}
              className={`px-4 py-1.5 text-xs font-bold rounded-lg transition flex items-center gap-1.5 cursor-pointer ${
                activeTab === "settings"
                  ? "bg-[#ffff00] text-black shadow-2xs"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              }`}
            >
              <FiSettings size={13} />
              <span>Wheel & Probability Settings</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab("history")}
              className={`px-4 py-1.5 text-xs font-bold rounded-lg transition flex items-center gap-1.5 cursor-pointer ${
                activeTab === "history"
                  ? "bg-[#ffff00] text-black shadow-2xs"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              }`}
            >
              <FiList size={13} />
              <span>Spin History Logs</span>
            </button>
          </div>

          {/* ================================================================= */}
          {/* TAB 1: SETTINGS & VISUAL WHEEL SIMULATOR */}
          {/* ================================================================= */}
          {activeTab === "settings" && (
            <div className="space-y-6">
              {settingsLoading ? (
                <div className="bg-white rounded-2xl p-12 text-center border border-gray-200">
                  <div className="w-8 h-8 border-3 border-[#ffff00] border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                  <p className="text-xs text-gray-500 font-medium">Loading Spin Wheel settings...</p>
                </div>
              ) : (
                <form onSubmit={handleSaveSettings} className="space-y-6">
                  {/* Visual Wheel + Test Simulator Card */}
                  <div className="bg-white rounded-2xl shadow-xs border border-gray-200 p-5 sm:p-6">
                    <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
                      {/* Left: Wheel Graphic */}
                      <div className="flex flex-col items-center justify-center relative">
                        {/* Wheel Top Pointer */}
                        <div className="absolute top-2 z-10 w-0 h-0 border-l-[14px] border-l-transparent border-r-[14px] border-r-transparent border-t-[22px] border-t-yellow-400 drop-shadow-md" />
                        
                        <div className="w-64 h-64 sm:w-72 sm:h-72 p-2">
                          {renderWheelSvg()}
                        </div>

                        {/* Test Spin Button */}
                        <div className="mt-4 flex items-center gap-3">
                          <button
                            type="button"
                            onClick={handleTestSpin}
                            disabled={spinning}
                            className="px-5 py-2 bg-gradient-to-r from-amber-400 to-yellow-400 hover:from-amber-500 hover:to-yellow-500 text-black font-extrabold text-xs rounded-xl shadow-md transition flex items-center gap-2 cursor-pointer disabled:opacity-50"
                          >
                            <FiPlay className={spinning ? "animate-spin" : ""} />
                            <span>{spinning ? "Spinning Wheel..." : "Test Spin Simulator"}</span>
                          </button>
                        </div>

                        {simulationResult && (
                          <div className="mt-3 text-center animate-in fade-in">
                            <span className="text-xs font-bold text-gray-700">
                              Landed on:{" "}
                              <strong className="text-purple-700 font-mono">
                                Slice #{simulationResult.sliceIndex} (+{simulationResult.points} PTS)
                              </strong>
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Right: 10 Slices Point Values Editor */}
                      <div className="flex-1 w-full space-y-4">
                        <div className="border-b border-gray-100 pb-2">
                          <h3 className="text-sm font-black text-gray-900 flex items-center gap-2">
                            <span>🎯</span>
                            <span>10 Slices Point Configuration</span>
                          </h3>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Specify the exact event point value awarded for each of the 10 fortune wheel slices.
                          </p>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                          {slices.map((pts, idx) => {
                            const sliceColor = SLICE_COLORS[idx % SLICE_COLORS.length];
                            return (
                              <div
                                key={idx}
                                className="bg-gray-50 border border-gray-200 rounded-xl p-2.5 flex flex-col gap-1.5 focus-within:border-yellow-400 focus-within:bg-white transition"
                              >
                                <div className="flex items-center justify-between">
                                  <span className="text-[10px] font-mono font-bold text-gray-400 uppercase">
                                    Slice #{idx}
                                  </span>
                                  <span
                                    className="w-2.5 h-2.5 rounded-full"
                                    style={{ backgroundColor: sliceColor }}
                                  />
                                </div>
                                <div className="flex items-center gap-1">
                                  <input
                                    type="number"
                                    min={0}
                                    max={10000}
                                    value={pts}
                                    onChange={(e) => handleSliceChange(idx, e.target.value)}
                                    className="w-full bg-transparent font-mono font-black text-sm text-gray-900 focus:outline-none"
                                  />
                                  <span className="text-[10px] text-gray-400 font-semibold">pts</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        <div className="bg-amber-50/60 border border-amber-200 rounded-xl p-3 text-xs text-amber-900 flex items-start gap-2">
                          <FiInfo className="h-4 w-4 shrink-0 text-amber-600 mt-0.5" />
                          <span>
                            Tip: Slices with <strong>0 pts</strong> represent loss/miss zones. High-tier rewards (e.g. 500 or 1000 pts) are unlocked based on probability settings below.
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Probability Distribution Card */}
                  <div className="bg-white rounded-2xl shadow-xs border border-gray-200 p-5 sm:p-6 space-y-5">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-gray-100 pb-3">
                      <div>
                        <h3 className="text-sm font-black text-gray-900 flex items-center gap-2">
                          <FiPercent className="h-4 w-4 text-purple-600" />
                          <span>Win Probability Percentages (6 Tiers)</span>
                        </h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Control how frequently users land on different point rewards. Total must equal 100%.
                        </p>
                      </div>

                      <div className="flex items-center gap-2">
                        <span
                          className={`text-xs font-mono font-black px-3 py-1 rounded-xl border ${
                            isProbabilityValid
                              ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                              : "bg-red-50 text-red-700 border-red-300 animate-pulse"
                          }`}
                        >
                          Total: {totalProbability}%
                        </span>

                        <button
                          type="button"
                          onClick={handleAutoBalance}
                          className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-800 text-xs font-bold rounded-lg transition cursor-pointer"
                        >
                          Auto-Balance
                        </button>
                        <button
                          type="button"
                          onClick={handleResetProbabilities}
                          className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-800 text-xs font-bold rounded-lg transition cursor-pointer"
                        >
                          Reset Defaults
                        </button>
                      </div>
                    </div>

                    {!isProbabilityValid && (
                      <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2 text-xs text-red-800 font-bold">
                        <FiAlertTriangle className="h-4 w-4 text-red-600 shrink-0" />
                        <span>
                          Warning: Probabilities total {totalProbability}%. Adjust sliders or click "Auto-Balance" to make it exactly 100% before saving.
                        </span>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-xs">
                      {/* 1. Loss / 0 Pts */}
                      <div className="bg-gray-50 border border-gray-200 rounded-xl p-3.5 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-gray-700">0 PTS (Loss / Miss)</span>
                          <span className="font-mono font-black text-gray-900 bg-white px-2 py-0.5 rounded border border-gray-200">
                            {pointProbabilities.lossPercentage}%
                          </span>
                        </div>
                        <input
                          type="range"
                          min={0}
                          max={100}
                          value={pointProbabilities.lossPercentage}
                          onChange={(e) =>
                            setPointProbabilities({
                              ...pointProbabilities,
                              lossPercentage: Number(e.target.value),
                            })
                          }
                          className="w-full accent-yellow-500 cursor-pointer"
                        />
                        <p className="text-[11px] text-gray-400">Baseline miss chance</p>
                      </div>

                      {/* 2. 10 Points */}
                      <div className="bg-blue-50/50 border border-blue-200 rounded-xl p-3.5 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-blue-900">+10 Points</span>
                          <span className="font-mono font-black text-blue-900 bg-white px-2 py-0.5 rounded border border-blue-200">
                            {pointProbabilities.tenPointsPercentage}%
                          </span>
                        </div>
                        <input
                          type="range"
                          min={0}
                          max={100}
                          value={pointProbabilities.tenPointsPercentage}
                          onChange={(e) =>
                            setPointProbabilities({
                              ...pointProbabilities,
                              tenPointsPercentage: Number(e.target.value),
                            })
                          }
                          className="w-full accent-blue-600 cursor-pointer"
                        />
                        <p className="text-[11px] text-blue-500">Standard common win</p>
                      </div>

                      {/* 3. 50 Points */}
                      <div className="bg-emerald-50/50 border border-emerald-200 rounded-xl p-3.5 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-emerald-900">+50 Points</span>
                          <span className="font-mono font-black text-emerald-900 bg-white px-2 py-0.5 rounded border border-emerald-200">
                            {pointProbabilities.fiftyPointsPercentage}%
                          </span>
                        </div>
                        <input
                          type="range"
                          min={0}
                          max={100}
                          value={pointProbabilities.fiftyPointsPercentage}
                          onChange={(e) =>
                            setPointProbabilities({
                              ...pointProbabilities,
                              fiftyPointsPercentage: Number(e.target.value),
                            })
                          }
                          className="w-full accent-emerald-600 cursor-pointer"
                        />
                        <p className="text-[11px] text-emerald-600">Uncommon win</p>
                      </div>

                      {/* 4. 100 Points */}
                      <div className="bg-purple-50/50 border border-purple-200 rounded-xl p-3.5 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-purple-900">+100 Points</span>
                          <span className="font-mono font-black text-purple-900 bg-white px-2 py-0.5 rounded border border-purple-200">
                            {pointProbabilities.hundredPointsPercentage}%
                          </span>
                        </div>
                        <input
                          type="range"
                          min={0}
                          max={100}
                          value={pointProbabilities.hundredPointsPercentage}
                          onChange={(e) =>
                            setPointProbabilities({
                              ...pointProbabilities,
                              hundredPointsPercentage: Number(e.target.value),
                            })
                          }
                          className="w-full accent-purple-600 cursor-pointer"
                        />
                        <p className="text-[11px] text-purple-600">Rare reward tier</p>
                      </div>

                      {/* 5. 500 Points */}
                      <div className="bg-pink-50/50 border border-pink-200 rounded-xl p-3.5 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-pink-900">+500 Points</span>
                          <span className="font-mono font-black text-pink-900 bg-white px-2 py-0.5 rounded border border-pink-200">
                            {pointProbabilities.fiveHundredPointsPercentage}%
                          </span>
                        </div>
                        <input
                          type="range"
                          min={0}
                          max={100}
                          value={pointProbabilities.fiveHundredPointsPercentage}
                          onChange={(e) =>
                            setPointProbabilities({
                              ...pointProbabilities,
                              fiveHundredPointsPercentage: Number(e.target.value),
                            })
                          }
                          className="w-full accent-pink-600 cursor-pointer"
                        />
                        <p className="text-[11px] text-pink-600">Epic jackpot slice</p>
                      </div>

                      {/* 6. 1000 Points */}
                      <div className="bg-amber-50/60 border border-amber-300 rounded-xl p-3.5 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-amber-900">👑 +1000 Points (Grand)</span>
                          <span className="font-mono font-black text-amber-900 bg-white px-2 py-0.5 rounded border border-amber-300">
                            {pointProbabilities.thousandPointsPercentage}%
                          </span>
                        </div>
                        <input
                          type="range"
                          min={0}
                          max={100}
                          value={pointProbabilities.thousandPointsPercentage}
                          onChange={(e) =>
                            setPointProbabilities({
                              ...pointProbabilities,
                              thousandPointsPercentage: Number(e.target.value),
                            })
                          }
                          className="w-full accent-amber-500 cursor-pointer"
                        />
                        <p className="text-[11px] text-amber-700 font-bold">Ultra-rare grand jackpot</p>
                      </div>
                    </div>
                  </div>

                  {/* General Game Rules */}
                  <div className="bg-white rounded-2xl shadow-xs border border-gray-200 p-5 sm:p-6 space-y-4">
                    <div className="border-b border-gray-100 pb-3">
                      <h3 className="text-sm font-black text-gray-900">General Rules & App Controls</h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Set spin animation time, cooldown between plays, and mini-game status in user app.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
                      <div>
                        <label className="block text-gray-700 font-bold mb-1">
                          Spin Animation Duration (Seconds)
                        </label>
                        <input
                          type="number"
                          min={1}
                          max={60}
                          required
                          value={formSettings.spinTime}
                          onChange={(e) =>
                            setFormSettings({ ...formSettings, spinTime: Number(e.target.value) })
                          }
                          className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-gray-900 focus:outline-none focus:border-yellow-400 focus:bg-white"
                        />
                        <p className="text-[11px] text-gray-400 mt-1">Default: 5 seconds</p>
                      </div>

                      <div>
                        <label className="block text-gray-700 font-bold mb-1">
                          Cooldown Period (Seconds)
                        </label>
                        <input
                          type="number"
                          min={0}
                          max={86400}
                          required
                          value={formSettings.cooldownSeconds}
                          onChange={(e) =>
                            setFormSettings({
                              ...formSettings,
                              cooldownSeconds: Number(e.target.value),
                            })
                          }
                          className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-gray-900 focus:outline-none focus:border-yellow-400 focus:bg-white"
                        />
                        <p className="text-[11px] text-gray-400 mt-1">Default: 180 seconds (3 mins)</p>
                      </div>

                      <div>
                        <label className="block text-gray-700 font-bold mb-1">Game Status in App</label>
                        <button
                          type="button"
                          onClick={() =>
                            setFormSettings({ ...formSettings, isActive: !formSettings.isActive })
                          }
                          className={`w-full py-2 px-3 rounded-xl font-bold flex items-center justify-center gap-1.5 border transition cursor-pointer ${
                            formSettings.isActive
                              ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                              : "bg-red-50 text-red-700 border-red-300"
                          }`}
                        >
                          {formSettings.isActive ? "✅ Active in App" : "❌ Disabled"}
                        </button>
                        <p className="text-[11px] text-gray-400 mt-1">Toggle visibility for users</p>
                      </div>
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="flex items-center justify-end gap-3 pt-2">
                    <button
                      type="submit"
                      disabled={savingSettings || !isProbabilityValid}
                      className="px-6 py-2.5 bg-[#ffff00] hover:bg-[#e6e600] disabled:opacity-50 text-black font-extrabold text-xs rounded-xl shadow-xs transition flex items-center gap-2 cursor-pointer"
                    >
                      {savingSettings ? (
                        <>
                          <FiRefreshCw className="h-4 w-4 animate-spin" />
                          <span>Saving Changes...</span>
                        </>
                      ) : (
                        <>
                          <FiSave className="h-4 w-4" />
                          <span>Save Spin Wheel Settings</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* ================================================================= */}
          {/* TAB 2: AUDIT HISTORY LOGS */}
          {/* ================================================================= */}
          {activeTab === "history" && (
            <div className="space-y-4">
              {/* Search & Filter Toolbar */}
              <div className="bg-white rounded-2xl shadow-xs border border-gray-200 p-3.5 flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="relative flex-1 max-w-md">
                  <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 h-3.5 w-3.5" />
                  <input
                    type="text"
                    placeholder="Search username, email, sessionId..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-9 pr-4 py-1.5 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-900 placeholder-gray-400 focus:outline-none focus:border-yellow-400 focus:bg-white"
                  />
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPage(1);
                    }}
                    className="bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-700 px-3 py-1.5 focus:outline-none focus:border-yellow-400"
                  >
                    <option value="all">All Status</option>
                    <option value="completed">Completed</option>
                    <option value="started">Started</option>
                    <option value="disqualified">Disqualified</option>
                  </select>

                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      setPage(1);
                    }}
                    className="bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-700 px-2.5 py-1.5 focus:outline-none focus:border-yellow-400"
                    title="Start Date"
                  />

                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => {
                      setEndDate(e.target.value);
                      setPage(1);
                    }}
                    className="bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-700 px-2.5 py-1.5 focus:outline-none focus:border-yellow-400"
                    title="End Date"
                  />
                </div>
              </div>

              {/* History Table */}
              <div className="bg-white rounded-2xl shadow-xs border border-gray-200 overflow-hidden">
                {historyLoading ? (
                  <div className="p-12 text-center">
                    <div className="w-8 h-8 border-3 border-[#ffff00] border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                    <p className="text-gray-500 text-xs font-semibold">Loading spin history records...</p>
                  </div>
                ) : history.length === 0 ? (
                  <div className="p-12 text-center">
                    <FiInfo className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-800 font-bold text-sm">No Spin Wheel records found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse text-xs">
                      <thead>
                        <tr className="border-b border-gray-200 bg-gray-50 text-gray-500 font-bold uppercase tracking-wider text-[11px]">
                          <th className="py-3 px-4">Session ID</th>
                          <th className="py-3 px-4">Player</th>
                          <th className="py-3 px-4 text-center">Target Slice</th>
                          <th className="py-3 px-4 text-right">Points Won</th>
                          <th className="py-3 px-4 text-center">Duration</th>
                          <th className="py-3 px-4 text-center">Status</th>
                          <th className="py-3 px-4">Timestamp</th>
                          <th className="py-3 px-4 text-center">Details</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {history.map((h, idx) => {
                          const sid = h.sessionId || h.session_id || h.id;
                          const shortSid = sid ? `${String(sid).substring(0, 12)}...` : "—";
                          const isCompleted =
                            h.status === "completed" || h.status === "Claimed" || h.status === "claimed";
                          const pts =
                            h.claimedPoints ?? h.pointsAwarded ?? h.targetPoints ?? h.points ?? 0;
                          const targetSliceIdx = h.targetIndex ?? h.sliceIndex ?? "—";
                          const userId = h.userId || h.user_id || h.user?.id;
                          const userName =
                            h.username ||
                            h.userName ||
                            h.user?.username ||
                            h.userEmail ||
                            `User #${userId || "—"}`;

                          return (
                            <tr key={sid || idx} className="hover:bg-gray-50/70 transition duration-150">
                              <td className="py-3.5 px-4 whitespace-nowrap font-mono text-gray-700">
                                <div className="flex items-center gap-1.5">
                                  <span>{shortSid}</span>
                                  {sid && (
                                    <button
                                      onClick={() => copySessionId(sid)}
                                      className="text-gray-400 hover:text-black p-1 rounded transition cursor-pointer"
                                      title="Copy ID"
                                    >
                                      {copiedSessionId === sid ? (
                                        <FiCheck className="h-3.5 w-3.5 text-emerald-600" />
                                      ) : (
                                        <FiCopy className="h-3.5 w-3.5" />
                                      )}
                                    </button>
                                  )}
                                </div>
                              </td>

                              <td className="py-3.5 px-4 whitespace-nowrap">
                                {userId ? (
                                  <Link
                                    to={`/user/${userId}`}
                                    className="font-bold text-gray-900 hover:text-amber-800 transition"
                                  >
                                    {userName}
                                  </Link>
                                ) : (
                                  <span className="text-gray-800 font-semibold">{userName}</span>
                                )}
                              </td>

                              <td className="py-3.5 px-4 whitespace-nowrap text-center">
                                <span className="px-2 py-0.5 bg-gray-100 border border-gray-200 rounded font-mono font-bold text-gray-700 text-[11px]">
                                  Slice #{targetSliceIdx}
                                </span>
                              </td>

                              <td className="py-3.5 px-4 whitespace-nowrap text-right font-mono font-bold text-purple-700">
                                +{Number(pts).toLocaleString()} pts
                              </td>

                              <td className="py-3.5 px-4 whitespace-nowrap text-center text-gray-600 font-mono">
                                {h.spinTime ?? h.gameTime ?? 5}s
                              </td>

                              <td className="py-3.5 px-4 whitespace-nowrap text-center">
                                <span
                                  className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                    isCompleted
                                      ? "bg-emerald-50 text-emerald-700 border border-emerald-300"
                                      : h.status === "started"
                                      ? "bg-blue-50 text-blue-700 border border-blue-300"
                                      : "bg-gray-100 text-gray-600 border border-gray-200"
                                  }`}
                                >
                                  {h.status || "Unknown"}
                                </span>
                              </td>

                              <td className="py-3.5 px-4 whitespace-nowrap text-gray-500 text-[11px]">
                                {formatDateTime(h.createdAt || h.startedAt || h.timestamp)}
                              </td>

                              <td className="py-3.5 px-4 whitespace-nowrap text-center">
                                <button
                                  onClick={() => setSelectedSessionModal(h)}
                                  className="p-1.5 rounded-lg bg-gray-100 hover:bg-[#ffff00] text-gray-700 hover:text-black transition cursor-pointer"
                                  title="View Session Details"
                                >
                                  <FiEye className="h-3.5 w-3.5" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Pagination */}
                <div className="p-3.5 bg-gray-50 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-600">
                  <span>Total: {pagination.total} records</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1 || historyLoading}
                      className="px-3 py-1 bg-white border border-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed shadow-2xs"
                    >
                      Prev
                    </button>
                    <span className="font-bold text-gray-800">
                      Page {page} of {pagination.totalPages}
                    </span>
                    <button
                      onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                      disabled={page >= pagination.totalPages || historyLoading}
                      className="px-3 py-1 bg-white border border-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed shadow-2xs"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Session Details Modal */}
      {selectedSessionModal && (
        <GameSessionDetailModal
          session={selectedSessionModal}
          gameType="spinwheel"
          onClose={() => setSelectedSessionModal(null)}
        />
      )}
    </div>
  );
}
